import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Bot, ChevronDown, ExternalLink, FolderKanban, GitBranch, GraduationCap, History, Home,
  LayoutDashboard, ListTodo, LogOut, Menu, Monitor, Moon, PenTool, Plus, Search, Sun, X,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import { uitloggen } from '../lib/api'
import { initialen } from '../lib/opmaak'
import { navigeer, useKop, useRoute } from '../lib/router'
import { nieuweTaak, useBasis, useStaat, zet } from '../lib/staat'
import { kiesThema, THEMA_LABELS, useThema, type ThemaKeuze } from '../lib/thema'
import { Knop, Logo, Punt } from './ui'
import TaakLade from './taak/TaakLade'
import NieuweTaak from './taak/NieuweTaak'
import Zoeken from './Zoeken'
import Meldingen from './Meldingen'

/* ===========================================================================
 *  Het geraamte -- dat van het dashboard
 *
 *  Casper: "zelfde UI als dashboard en de academy". Dus geen eigen zijbalk
 *  meer, maar wat het dashboard heeft: een witte balk van 48 hoog met links
 *  de menuknop en het logo op zijn donkere bordje, daarnaast de naam van
 *  het scherm, en rechts zoeken en wie je bent. De menuknop opent hetzelfde
 *  grote menu als in het dashboard, in groepen, met een filter bovenaan.
 *
 *  Sneltoetsen: Ctrl+K of / zoeken, C nieuwe taak, Esc dicht.
 * =========================================================================== */

export default function Shell({ children }: { children: ReactNode }) {
  const openTaakId = useStaat((s) => s.openTaak)
  const nieuw = useStaat((s) => s.nieuweTaak)
  const zoekOpen = useStaat((s) => s.zoekOpen)
  const { delen, pad } = useRoute()
  const kop = useKop()
  const [menu, setMenu] = useState(false)

  const hierProject = delen[0] === 'p' ? delen[1] : null

  /* Het menu gaat dicht zodra je ergens heen gaat. */
  useEffect(() => { setMenu(false) }, [pad])

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      const doel = e.target as HTMLElement | null
      const typt = doel && (doel.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(doel.tagName))
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        zet({ zoekOpen: true })
        return
      }
      if (typt || e.ctrlKey || e.metaKey || e.altKey) return
      if (delen[0] === 'wb') return
      if (e.key === '/') { e.preventDefault(); zet({ zoekOpen: true }) }
      if (e.key.toLowerCase() === 'c') { e.preventDefault(); nieuweTaak({ projectId: hierProject }) }
    }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [hierProject, delen])

  const lagen = (
    <>
      {openTaakId && <TaakLade id={openTaakId} />}
      {nieuw && <NieuweTaak {...nieuw} />}
      {zoekOpen && <Zoeken />}
      <Meldingen />
    </>
  )

  /* Het whiteboard krijgt het hele scherm. */
  if (delen[0] === 'wb') return <>{children}{lagen}</>

  return (
    <div className="shell">
      <header className="topbalk">
        <button type="button" className="menuknop" aria-expanded={menu} aria-label="Menu"
          onClick={() => setMenu((x) => !x)}>
          {menu ? <X size={18} /> : <Menu size={18} />}
        </button>
        <button type="button" className="topbalk-merk" onClick={() => navigeer('/')} title="Naar het overzicht">
          <Logo breedte={96} />
          <span className="topbalk-app">Projects</span>
        </button>
        {kop.titel && (
          <div className="topbalk-plek">
            <span className="topbalk-punt">·</span>
            <b>{kop.titel}</b>
            {kop.sub && <span className="topbalk-sub">{kop.sub}</span>}
          </div>
        )}
        <span className="topbalk-rek" />
        <div className="topbalk-rechts">
          <button type="button" className="search-trigger" onClick={() => zet({ zoekOpen: true })} aria-label="Zoeken">
            <Search size={15} />
            <span className="zoek-label">Zoeken…</span>
            <kbd>Ctrl K</kbd>
          </button>
          <Knop soort="hoofd" icoon={<Plus size={16} />} onClick={() => nieuweTaak({ projectId: hierProject })} title="Nieuwe taak (C)">
            <span className="verberg-smal">Nieuwe taak</span>
          </Knop>
          <IkMenu />
        </div>
      </header>

      <main id="inhoud" className="werkvlak">{children}</main>

      {menu && <Lanceerder onSluit={() => setMenu(false)} />}
      {lagen}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Het grote menu, zoals in het dashboard
 * ------------------------------------------------------------------ */

interface Plek { naar: string; label: string; icoon: ReactNode; teller?: number; extern?: boolean }

function Lanceerder({ onSluit }: { onSluit: () => void }) {
  const { projecten } = useBasis()
  const { pad } = useRoute()
  const [filter, setFilter] = useState('')
  const veld = useRef<HTMLInputElement>(null)

  useEffect(() => {
    veld.current?.focus()
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') onSluit() }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [onSluit])

  const groepen = useMemo(() => {
    const lopend = projecten.filter((p) => !p.archief && p.status !== 'afgerond')
      .sort((a, b) => (b.telling?.laatst ?? 0) - (a.telling?.laatst ?? 0))
    const g: { titel: string; plekken: Plek[] }[] = [
      { titel: 'Werk', plekken: [
        { naar: '/', label: 'Overzicht', icoon: <Home size={17} /> },
        { naar: '/mijn', label: 'Mijn werk', icoon: <ListTodo size={17} /> },
        { naar: '/projecten', label: 'Projecten', icoon: <FolderKanban size={17} /> },
        { naar: '/whiteboards', label: 'Whiteboards', icoon: <PenTool size={17} /> },
        { naar: '/ai', label: 'Assistent', icoon: <Bot size={17} /> },
      ] },
      { titel: 'Code', plekken: [
        { naar: '/code', label: 'Repositories', icoon: <GitBranch size={17} /> },
        { naar: '/changelog', label: 'Changelog', icoon: <History size={17} /> },
      ] },
      { titel: 'Projecten', plekken: [
        ...lopend.map((p) => ({
          naar: `/p/${p.id}/bord`, label: p.naam, icoon: <Punt kleur={p.kleur} />,
          teller: p.telling?.teLaat || undefined,
        })),
        { naar: '/projecten?nieuw=1', label: 'Nieuw project', icoon: <Plus size={17} /> },
      ] },
      { titel: 'Truckwash1', plekken: [
        { naar: 'https://truckwash-workspace.com/', label: 'Het dashboard', icoon: <LayoutDashboard size={17} />, extern: true },
        { naar: 'https://academy.truckwash-workspace.com/', label: 'Academy', icoon: <GraduationCap size={17} />, extern: true },
      ] },
    ]
    const f = filter.trim().toLowerCase()
    return f ? g.map((x) => ({ ...x, plekken: x.plekken.filter((p) => p.label.toLowerCase().includes(f)) })).filter((x) => x.plekken.length) : g
  }, [projecten, filter])

  const huidig = (naar: string) => {
    if (naar === '/') return pad === '/'
    if (naar.startsWith('/p/')) return pad.startsWith(naar.replace(/\/bord$/, ''))
    return !naar.includes('?') && pad.startsWith(naar)
  }

  return createPortal(
    <>
      <div className="launcher-sluier" onClick={onSluit} />
      <nav className="launcher" aria-label="Alles in Projects">
        <div className="launcher-kop">
          <h2>Projects</h2>
          <p>Alles wat je hier kunt doen.</p>
          <div className="zoekveld launcher-filter">
            <Search size={15} />
            <input ref={veld} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter dit menu…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const eerste = groepen[0]?.plekken[0]
                  if (eerste) ga(eerste)
                }
              }} />
          </div>
        </div>
        <div className="launcher-body">
          {groepen.map((g) => (
            <div key={g.titel} className="launcher-groep">
              <h3>{g.titel}</h3>
              {g.plekken.map((p) => (
                <button key={p.naar} type="button" className="launcher-item" aria-current={huidig(p.naar) ? 'page' : undefined}
                  onClick={() => ga(p)}>
                  {p.icoon}
                  <span className="naam">{p.label}</span>
                  {!!p.teller && <span className="teller" title="Te laat">{p.teller}</span>}
                  {p.extern && <ExternalLink size={13} />}
                </button>
              ))}
            </div>
          ))}
          {!groepen.length && <p className="stil">Niets gevonden voor “{filter}”.</p>}
        </div>
      </nav>
    </>,
    document.body,
  )

  function ga(p: Plek) {
    if (p.extern) { window.open(p.naar, '_blank', 'noopener,noreferrer'); return }
    onSluit()
    navigeer(p.naar)
  }
}

/* ------------------------------------------------------------------ *
 *  Wie je bent, het thema, en weg
 * ------------------------------------------------------------------ */

function IkMenu() {
  const ik = useStaat((s) => s.ik)!
  const { keuze } = useThema()
  const [open, setOpen] = useState(false)
  const knop = useRef<HTMLButtonElement>(null)
  const [plek, setPlek] = useState<{ top: number; right: number } | null>(null)

  useEffect(() => {
    if (!open || !knop.current) return
    const r = knop.current.getBoundingClientRect()
    setPlek({ top: r.bottom + 6, right: window.innerWidth - r.right })
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [open])

  const ICOON: Record<ThemaKeuze, ReactNode> = { systeem: <Monitor size={15} />, licht: <Sun size={15} />, donker: <Moon size={15} /> }
  const rol = ik.rollen.includes('management') ? 'Management' : ik.rollen.includes('developer') ? 'Ontwikkelaar' : ''

  return (
    <>
      <button ref={knop} type="button" className={`menu-trigger ${open ? 'open' : ''}`} onClick={() => setOpen((x) => !x)}
        aria-label="Jouw menu">
        <span className="menu-av">{initialen(ik.naam)}</span>
        <ChevronDown size={14} className="pijl" />
      </button>
      {open && plek && createPortal(
        <>
          <div className="uitklap-vang" onMouseDown={() => setOpen(false)} />
          <div className="menu-panel" style={{ top: plek.top, right: plek.right }}>
            <div className="menu-persoon-kop">
              <strong>{ik.naam}</strong>
              <span>{[rol, ik.email].filter(Boolean).join(' · ')}</span>
            </div>
            <div className="menu-scheiding" />
            <div className="menu-groep-titel">Weergave</div>
            {(['systeem', 'licht', 'donker'] as ThemaKeuze[]).map((k) => (
              <button key={k} type="button" className={`menu-item ${keuze === k ? 'aan' : ''}`} onClick={() => kiesThema(k)}>
                <span className="ico">{ICOON[k]}</span>
                <span className="tekst"><strong>{THEMA_LABELS[k]}</strong></span>
                {keuze === k && <span className="menu-vink">✓</span>}
              </button>
            ))}
            <div className="menu-scheiding" />
            <a className="menu-item" href="https://truckwash-workspace.com/" target="_blank" rel="noopener noreferrer">
              <span className="ico"><LayoutDashboard size={15} /></span>
              <span className="tekst"><strong>Naar het dashboard</strong></span>
            </a>
            <button type="button" className="menu-item danger" onClick={() => { void uitloggen().then(() => location.assign('/')) }}>
              <span className="ico"><LogOut size={15} /></span>
              <span className="tekst"><strong>Uitloggen</strong></span>
            </button>
          </div>
        </>,
        document.body,
      )}
    </>
  )
}
